import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSupabaseClient } from '../lib/supabase';

type StepType = 'radio' | 'textarea';

interface RadioStep {
  id: string;
  question: string;
  type: 'radio';
  options: string[];
  value: string;
  setValue: (value: string) => void;
  required: boolean;
}

interface TextareaStep {
  id: string;
  question: string;
  type: 'textarea';
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  required: boolean;
  subtext?: string[];
}

type Step = RadioStep | TextareaStep;

export default function ExperienceForm({ onComplete }: { onComplete?: () => void }) {
  const supabase = getSupabaseClient();

  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should start at the last step (coming back from next form)
  const startAtLastStep = (location.state as { returnToOldExperience?: boolean })?.returnToOldExperience;
  const TOTAL_STEPS = 7; // Number of steps in this form

  const [currentStep, setCurrentStep] = useState(startAtLastStep ? TOTAL_STEPS - 1 : 0);

  // Scroll to top when coming back from next form
  useEffect(() => {
    if (startAtLastStep) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [years, setYears] = useState('');
  const [timeType, setTimeType] = useState('');
  const [ceremonies, setCeremonies] = useState('');
  const [medical, setMedical] = useState('');
  const [team, setTeam] = useState('');
  const [gender, setGender] = useState('');
  const [relationship, setRelationship] = useState('');
  const [workStyle, setWorkStyle] = useState('');

  // Load existing data on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('Auth error:', authError);
          setLoading(false);
          return;
        }

        // Try to load existing professional data
        const { data: existingData, error } = await supabase
          .from('guide_professional')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error loading professional data:', error);
        } else if (existingData) {
          // Populate form with existing data
          setYears(existingData.years_practicing || '');
          setTimeType(existingData.employment_type || '');
          setCeremonies(existingData.ceremonies_count || '');
          setMedical(existingData.has_medical_director ? 'Yes' : 'No');
          setTeam(existingData.works_with_other_facilitators ? 'I typically work with other facilitators' : 'I typically work on my own');
          setGender(existingData.gender_id || '');
          setRelationship(existingData.personal_relationship || '');
          setWorkStyle(existingData.modality || '');
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, []);

  const steps: Step[] = [
    {
      id: 'years',
      question: 'How long have you been facilitating this kind of work?',
      type: 'radio',
      options: ['Less than 2 years', '2–5 years', 'More than 5 years – I\'ve been facilitating this work for a long time'],
      value: years,
      setValue: setYears,
      required: true
    },
    {
      id: 'timeType',
      question: 'Is this work currently full-time or part-time for you?',
      type: 'radio',
      options: ['Full time', 'Part time'],
      value: timeType,
      setValue: setTimeType,
      required: true
    },
    {
      id: 'ceremonies',
      question: 'Approximately how many ceremonies or sessions have you facilitated?',
      type: 'radio',
      options: ['Less than 5', '5–15', '15–50', '50–100', 'More than 100'],
      value: ceremonies,
      setValue: setCeremonies,
      required: true
    },
    {
      id: 'medical',
      question: 'Do you currently collaborate with a licensed medical or mental health professional (such as a psychiatrist, therapist, psychologist, or medical director)?',
      type: 'radio',
      options: ['Yes', 'No'],
      value: medical,
      setValue: setMedical,
      required: true
    },
    {
      id: 'team',
      question: 'Do you usually facilitate on your own, or with other guides?',
      type: 'radio',
      options: ['I typically work with other facilitators', 'I typically work on my own'],
      value: team,
      setValue: setTeam,
      required: true
    },
    {
      id: 'gender',
      question: 'What is your gender?',
      type: 'radio',
      options: ['Male', 'Female', 'Transgender', 'Non-binary', 'Other', 'Prefer not to say'],
      value: gender,
      setValue: setGender,
      required: true
    },
    {
      id: 'relationship',
      question: 'Tell us about your personal relationship with psychedelics.',
      type: 'textarea',
      value: relationship,
      setValue: setRelationship,
      placeholder: 'Please keep your response focused on insight, growth, and your approach to the work, rather than specific details about any activities that may not be legal where you live.',
      required: false,
      subtext: [
        'How have these tools influenced your path as a facilitator? You\'re welcome to share your personal philosophy, the role these practices play in your life, and the experiences that shaped your approach to guiding. This helps us understand your values, grounding, and perspective.'
      ]
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // Auto-advance for radio buttons - DISABLED
  // useEffect(() => {
  //   if (currentStepData?.type === 'radio' && currentStepData.value && !saving) {
  //     const timer = setTimeout(() => {
  //       if (currentStep < steps.length - 1) {
  //         setCurrentStep(currentStep + 1);
  //       }
  //     }, 800); // Small delay for better UX
  //     return () => clearTimeout(timer);
  //   }
  // }, [currentStepData?.value, currentStep, steps.length, saving]);

  const canProceedToNext = () => {
    if (!currentStepData.required) return true;
    
    if (currentStepData.type === 'radio') {
      return !!currentStepData.value;
    } else if (currentStepData.type === 'textarea') {
      return !!currentStepData.value.trim();
    }
    return true;
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/onboarding'); // Go back to the onboarding router
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        alert('Authentication error. Please log in again.');
        return;
      }

      // Prepare the request body
      const requestBody = {
        years_practicing: years,
        employment_type: timeType,
        ceremonies_count: ceremonies,
        has_medical_director: medical === 'Yes',
        works_with_other_facilitators: team === 'I typically work with other facilitators',
        gender_id: gender,
        personal_relationship: relationship || undefined,
      };

      const { data, error } = await supabase.functions.invoke('guide-professional', {
        body: requestBody,
      });

      if (error) {
        console.error('Error saving professional data:', error);
        throw new Error(error.message || 'Failed to invoke edge function');
      }

      if (!data?.success) {
        const errorMessage = data?.error || 'Failed to save professional data';
        console.error('Edge function returned error:', data);
        throw new Error(errorMessage);
      }

      // Trigger the refresh function instead of navigating
      // Don't set saving to false here - let navigation handle it
      if (onComplete) {
        onComplete();
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      setSaving(false); // Only reset on error
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      await handleSubmit();
    }
  };

  const renderQuestion = () => {
    const step = currentStepData;

    if (step.type === 'radio') {
      return (
        <fieldset className="flex flex-col gap-3 w-full">
          <legend className="text-[42px] leading-[44px] font-medium mb-6 text-[#304031] font-recoleta">
            {step.question}
          </legend>
          {step.options.map((opt, index) => {
            const selected = step.value === opt;
            const optionId = `${step.id}-${index}`;
            return (
              <label
                key={opt}
                htmlFor={optionId}
                className={`flex items-start gap-3 rounded-xl border border-transparent px-4 py-3 text-base transition hover:bg-[#304031]/5 cursor-pointer ${
                  selected ? 'font-semibold text-[#2C2731]' : 'text-[#2C2731]'
                }`}
              >
                <input
                  id={optionId}
                  type="radio"
                  name={step.id}
                  checked={selected}
                  onChange={() => step.setValue(opt)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                    selected ? 'border-[#304031]' : 'border-[#304031]/40'
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      selected ? 'bg-[#304031]' : 'bg-transparent'
                    }`}
                  />
                </span>
                <span className="text-base text-[#2C2731]">{opt}</span>
              </label>
            );
          })}
        </fieldset>
      );
    }

    if (step.type === 'textarea') {
      return (
        <div className="w-full">
          <label className="mb-4 block text-[42px] leading-[44px] font-medium text-[#304031] font-recoleta">
            {step.question}
          </label>
          {step.subtext && step.subtext.length > 0 && (
            <div className="mb-8 space-y-4">
              {step.subtext.map((text, index) => (
                <p key={index} className="text-[18px] leading-[1.6] text-[#304031]/80">
                  {text}
                </p>
              ))}
            </div>
          )}
          <textarea
            rows={6}
            value={step.value}
            onChange={(e) => step.setValue(e.target.value)}
            placeholder={step.placeholder}
            className="w-full rounded-lg border border-[#a0978a] bg-[#fffcef] px-4 py-3 text-base text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]"
          />
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-6">
      <div className="mx-auto w-[90vw] py-12 text-[#304031]">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#304031]"></div>
            <span className="ml-4 text-base text-[#304031]/70">Loading your information...</span>
          </div>
      ) : (
        <>
          {/* Question content */}
          <div className="mb-8">
            {renderQuestion()}
          </div>

          {/* Navigation */}
          <div className="flex gap-4 items-center pt-4">
            <button
              onClick={handlePrevious}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-[#304031] px-6 py-3 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              ← Back
            </button>

            {/* Show continue button for all questions */}
            <button
              onClick={handleNext}
              disabled={!canProceedToNext() || saving}
              className={`flex items-center gap-2 rounded-full px-8 py-3 text-base font-semibold transition ${
                canProceedToNext() && !saving
                  ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {saving && (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#fef6d6] border-t-transparent"></div>
              )}
              {isLastStep ? (saving ? 'Saving...' : 'Continue') : 'Continue'} →
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
